import type React from "react"
import { useMemo, memo, useCallback, useState } from "react"
import { ReactComponent as PhoneIcon } from "../../../assets/icons/phone.svg"
import { ReactComponent as CheckIcon } from "../../../assets/icons/checked.svg"
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg"
import { ReactComponent as ClockIcon } from "../../../assets/icons/clock.svg"
import { twMerge } from "tailwind-merge"
import { Link } from "react-router-dom"
import { useAuthState } from "react-firebase-hooks/auth"
import { auth, functions } from "@utils/firebase"
import { useHttpsCallable } from "react-firebase-hooks/functions"
import { useQueryClient } from "react-query"
import { useGetUserBookings } from "@utils/hooks"
import BarStepsProgress from "@components/Progress/BarStepsProgress"

export interface BookingData {
  id: string
  start_time: string
  invitee: {
    timezone: string
  }
  event_memberships: Array<{
    user_name: string
  }>
}

export interface UserData {
  first_name?: string
  last_name?: string
  [key: string]: any
}

type DashboardBookCallProps = {
  data_booking: BookingData | null
  userData: UserData | null
}

const DashboardBookCall: React.FC<DashboardBookCallProps> = ({ data_booking, userData }) => {
  const [user] = useAuthState(auth)
  const queryClient = useQueryClient()
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const { refetch } = useGetUserBookings()

  const [cancelCalendlyBooking] = useHttpsCallable(functions, "user-on_cancel_canlendly_booking")

  const formmatedDateTimes = useMemo(() => {
    if (!data_booking?.start_time || !data_booking?.invitee.timezone) {
      return {
        start_date: "",
        start_time: "",
      }
    }

    const startDateTime = new Date(data_booking.start_time)
    const timezone = data_booking.invitee.timezone

    const start_date = startDateTime.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: timezone,
    })
    const start_time =
      startDateTime.toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: timezone,
      }) +
      " " +
      `(${timezone})`

    return {
      start_date,
      start_time,
    }
  }, [data_booking?.start_time, data_booking?.invitee.timezone])

  const handleCancelCall = useCallback(async () => {
    if (!data_booking || cancelling) return

    setCancelling(true)
    setCancelError(null)

    try {
      const uid = data_booking.id

      const req = await cancelCalendlyBooking({ event_uid: uid })

      const res = req?.data as { success?: boolean; alreadyCancelled?: boolean; message?: string }

      if (res?.success) {
        // Invalidate all relevant queries to ensure fresh data
        await queryClient.invalidateQueries(["users"])
        await queryClient.invalidateQueries(["userscalendly"])
        
        // Force refetch
        refetch()

        if (res.alreadyCancelled) {
          // Show a message that the call was already cancelled
          setCancelError("This call has already been cancelled")
          // After a delay, clear the error and let the UI update
          setTimeout(() => {
            setCancelError(null)
          }, 3000)
        }
      } else {
        setCancelError("Failed to cancel the call. Please try again.")
      }
    } catch (error: any) {
      console.error("Error cancelling call:", error)
      setCancelError(error?.message || "An error occurred while cancelling the call")
    } finally {
      setCancelling(false)
    }
  }, [data_booking, cancelCalendlyBooking, queryClient, refetch, cancelling])

  return (
    <div className="px-4 pb-6 lg:px-[80px] md:py-10 bg-pn_paper-3 flex flex-col gap-y-0 md:gap-y-6">
      <div className="w-full py-4 lg:py-0">
        <BarStepsProgress activeStep={3} />
      </div>

      <div className="pb-4 lg:pb-0">
        <div
          className={twMerge(
            "border bg-white border-pn_gray-200 rounded-xl flex flex-col",
            data_booking ? "px-6 py-6 gap-y-4 lg:px-[23px] lg:py-[23px] lg:gap-y-6" : "gap-y-6 p-6",
          )}
        >
          {!data_booking ? (
            <>
              <div className="flex flex-col gap-y-6">
                <div className="w-10 h-10 rounded-full bg-pn_paper-3 flex items-center justify-center pr-1">
                  <PhoneIcon />
                </div>
                <div className="flex-col flex gap-y-2">
                  <span className="text-pn-body-emphasis">Book Call with Adviser</span>
                  <span className="text-pn-callout-regular">
                    Book a free call with one of our expert advisers to find out exactly what you need, why you need it
                    and how to get started.
                  </span>
                </div>
              </div>
              <Link to="/schedule-call" className="btn-secondary-lg text-center">
                Book Call
              </Link>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-y-4 gap-x-4 lg:gap-x-6 flex-1">
                  <div className="w-[60px] h-[60px] rounded-full bg-pn_green-500 flex items-center justify-center">
                    <CheckIcon className="w-8 h-8" />
                  </div>
                  <div className="flex flex-col gap-y-2 lg:gap-y-1">
                    <span className="text-pn-body-large-emphasis">Your call is sheduled</span>
                    <span className="text-pn-body-small-regular text-pn_gray-600">
                      {user?.displayName || userData?.first_name
                        ? `${user?.displayName || userData?.first_name}, your call is scheduled with ${data_booking.event_memberships[0]?.user_name ?? "a member from Penny"
                        }`
                        : `Your call is scheduled with ${data_booking.event_memberships[0]?.user_name ?? "a member from Penny"
                        }`}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCancelCall}
                  className={twMerge(
                    "btn-tertiary-sm py-1 hidden lg:block !border-pn_red-500 !text-pn_red-500",
                    cancelling && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Cancel call"}
                </button>
              </div>

              <div className="flex flex-col lg:flex-row items-center gap-x-6">
                <div className="py-2 px-0 lg:px-4 lg:border lg:border-pn_gray-200 lg:bg-pn_white lg:rounded-2xl flex-1 w-full">
                  <div className="py-0 lg:py-[7px] flex items-center justify-between">
                    <div className="flex items-center gap-x-3">
                      <div className="bg-pn_paper-3 w-10 h-10 flex items-center justify-center rounded-full">
                        <CalendarIcon />
                      </div>
                      <span className="text-pn-body-small-regular text-pn_gray-950">Date</span>
                    </div>
                    <span className="text-pn-body-small-regular bg-pn_yellow-200 w-fit">
                      {formmatedDateTimes.start_date}
                    </span>
                  </div>
                </div>

                <div className="py-2 px-0 lg:px-4 lg:border lg:border-pn_gray-200 lg:bg-pn_white lg:rounded-2xl flex-1 w-full">
                  <div className="py-0 lg:py-[7px] flex items-center justify-between">
                    <div className="flex items-center gap-x-3">
                      <div className="bg-pn_paper-3 w-10 h-10 flex items-center justify-center rounded-full">
                        <ClockIcon />
                      </div>
                      <span className="text-pn-body-small-regular text-pn_gray-950">Time</span>
                    </div>
                    <span className="text-pn-body-small-regular bg-pn_yellow-200 w-fit">
                      {formmatedDateTimes.start_time}
                    </span>
                  </div>
                </div>
              </div>

              {cancelError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {cancelError}
                </div>
              )}

              {/* <Link to="/pension_space" className="btn-primary-lg text-center">
                Go to Penny Space
              </Link> */}

              <div className="py-1.5 flex lg:hidden items-center justify-center gap-x-6">
                <button 
                  onClick={handleCancelCall} 
                  className={twMerge(
                    "btn-tertiary-sm py-1 !border-pn_red-500 !text-pn_red-500",
                    cancelling && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Cancel call"}
                </button>
                {/* <button className="btn-secondary-sm py-1">Reschedule call</button> */}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(DashboardBookCall)
